import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Lock, User, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-brand-espresso">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex">
        <img
          src="https://images.unsplash.com/photo-1562367072-fea5c7eb8748?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Jakarta"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2a0d0e] via-[#421517]/80 to-transparent" />
        <div className="relative z-10 flex items-center gap-3 text-white">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#421517]">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold tracking-tight">SHOWTY HRIS</p>
            <p className="text-xs text-slate-400">PT Showty Global International</p>
          </div>
        </div>
        <div className="relative z-10 text-white">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight lg:text-5xl">
            Sistem Informasi<br />Kepegawaian Terpadu
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Absensi berbasis GPS, database karyawan, dan formulir HR dalam satu platform yang aman dan modern.
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-emerald-400">
            <ShieldCheck className="h-4 w-4" /> Data terenkripsi & terautentikasi
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md rounded-3xl border border-[#5a2427] bg-[#3a1214] p-8 shadow-2xl"
        >
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2 text-white">
              <Building2 className="h-6 w-6 text-primary" />
              <span className="font-heading text-lg font-bold">SHOWTY HRIS</span>
            </div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-white">Login Karyawan</h2>
          <p className="mt-1 text-sm text-slate-400">Masuk menggunakan NIK KTP atau email dan kata sandi Anda.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="nik" className="text-slate-300">NIK KTP / Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  id="nik"
                  data-testid="login-email-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="NIK 16 digit atau email"
                  className="border-slate-700 bg-slate-800 pl-10 text-white placeholder:text-slate-500"
                  autoComplete="username"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw" className="text-slate-300">Kata Sandi</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  id="pw"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Kata sandi dari HR"
                  className="border-slate-700 bg-slate-800 pl-10 text-white placeholder:text-slate-500"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {err && (
              <p data-testid="login-error" className="rounded-lg bg-rose-950/50 px-3 py-2 text-sm text-rose-300">
                {err}
              </p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="h-11 w-full bg-primary text-base font-semibold hover:bg-primary/90"
            >
              {loading ? "Memproses..." : "LOGIN"}
            </Button>
          </form>

          <p className="mt-6 rounded-lg border border-slate-800 bg-slate-800/40 px-4 py-3 text-center text-xs text-slate-400">
            Belum memiliki akun atau mengalami kendala login?<br />
            Hubungi HR PT Showty Global International.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
